import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ClinicalTrialsUIService } from '../../services/clinical-trials-ui.service';
import { ExpanderDirective } from '../../directives/expander.directive';
import { getUnansweredRequiredCriteriaCount, initCriteriaForm, initExtensionForm } from '../../utils';
import { IClinicalTrialSubject, IResearchSubject } from '../../interfaces';

@UntilDestroy()
@Component({
  selector: '[appClinicalTrialsExpander]',
  styleUrls: ['./clinical-trials-expander.component.scss'],
  templateUrl: './clinical-trials-expander.component.html'
})
export class ClinicalTrialsExpanderComponent extends ExpanderDirective implements OnInit {

  @Input()
  clinicalTrialSubject: IClinicalTrialSubject;

  @Input()
  index: number;

  criteriaForm: FormGroup;
  otherExtensions: fhir4.Extension[];
  researchSubjectForm: FormGroup;
  therapeuticAreasString: string;

  get qualityScore(): fhir4.Ratio {
    return this.qualityScoreForm?.value.valueRatio as fhir4.Ratio;
  }

  get qualityScoreForm(): FormGroup {
    return (this.researchSubjectForm?.get('extension') as FormArray).controls[0] as FormGroup;
  }

  get unansweredRequiredCriteria(): number {
    return getUnansweredRequiredCriteriaCount(this.clinicalTrialSubject.researchStudyDisplay.group, this.criteriaForm.value as fhir4.QuestionnaireResponse);
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly clinicalTrialsUIService: ClinicalTrialsUIService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.expanderSelector = `#collapse-${ this.index }`;
    this.therapeuticAreasString = this.clinicalTrialSubject.researchStudyDisplay.therapeuticAreas?.join(', ');
  }

  initResearchSubjectForm(): void {
    const { qualityScoreForm, otherExtensions } = initExtensionForm(this.clinicalTrialSubject.researchStudyDisplay.group, this.clinicalTrialSubject.researchSubject.extension);
    this.otherExtensions = otherExtensions;
    this.researchSubjectForm = new FormGroup({
      extension: new FormArray([qualityScoreForm]),
    });
  }

  save(): void {
    const editedResearchSubject: IResearchSubject = {
      ...this.clinicalTrialSubject.researchSubject,
      ...this.researchSubjectForm.value,
    };

    delete editedResearchSubject.meta;
    editedResearchSubject.extension = [...editedResearchSubject.extension, ...this.otherExtensions];

    this.clinicalTrialsUIService.updateClinicalTrialSubject(this.criteriaForm.value as fhir4.QuestionnaireResponse, editedResearchSubject).pipe(take(1)).subscribe(({questionnaireResponse, researchSubject}) => {
      this.clinicalTrialSubject.questionnaireResponse = questionnaireResponse;
      this.clinicalTrialSubject.researchSubject = researchSubject;

      this.changeDetectorRef.markForCheck();
    });
  }

  toggleClinicalTrialsExpander(event: Event): void {
    if (!this.clinicalTrialSubject.questionnaireResponse && !this.clinicalTrialSubject.researchStudyDisplay.group) {
      this.clinicalTrialsUIService.getCriteria(this.clinicalTrialSubject).pipe().subscribe(criteriaData => {
        this.clinicalTrialSubject.questionnaireResponse = criteriaData.questionnaireResponse;
        this.clinicalTrialSubject.researchStudyDisplay.group = criteriaData.group;
        this.initResearchSubjectForm();
        this.criteriaForm = initCriteriaForm(this.clinicalTrialSubject.researchStudyDisplay.group, this.clinicalTrialSubject.questionnaireResponse, this.clinicalTrialSubject.researchSubject);
        this.changeDetectorRef.markForCheck();
      });

      this.clinicalTrialsUIService.criteriaChanged$.pipe(untilDestroyed(this)).subscribe(item => {
        const updatedItemIDs = item.map(obj => obj.linkId);
        const itemsMinusUpdatedItems = (this.criteriaForm.value as fhir4.QuestionnaireResponse).item.filter(obj => !updatedItemIDs.includes(obj.linkId));
        this.criteriaForm.patchValue({
          item: [
            ...itemsMinusUpdatedItems,
            ...item,
          ]
        });
      });
    }
    this.toggleExpander(event);
  }
}
